import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';

import Swal from 'sweetalert2';
import {
  Pencil,
  Trash2,
  Plus,
  Copy
} from 'lucide-react';

function Forms() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/forms');
      setForms(response.data.forms || []);
    } catch (err) {
      setError('Failed to fetch forms');
      console.error('Error fetching forms:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleCreateForm = async () => {
    if (!formData.name) {
      alert('Please enter a form name');
      return;
    }

    try {
      // Create form without campaign_id - backend will assign to first campaign
      const response = await api.post('/admin/forms', formData);
      navigate(`/admin/forms/${response.data.form.id}/edit`);
    } catch (err) {
      console.error('Create form error:', err);
      alert(err.response?.data?.error || 'Failed to create form');
    }
  };

  const handleDeleteForm = async (formId, formName) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete "<strong>${formName || 'this form'}</strong>"<br><br>This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/forms/${formId}`);
        setSuccessMessage('Form has been deleted successfully.');
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
        fetchForms();
      } catch (err) {
        console.error('Delete form error:', err);
        const error = err.response?.data?.error || 'Failed to delete form';
        setErrorMessage(error);
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    }
  };

  const handleDuplicateForm = async (formId, formName) => {
    const newName = prompt(`Enter a name for the duplicated form:`, `${formName} (Copy)`);
    if (!newName) return;

    try {
      const response = await api.post(`/admin/forms/${formId}/duplicate`, { name: newName });
      if (response.data.form) {
        // Navigate to edit the new form
        navigate(`/admin/forms/${response.data.form.id}/edit`);
      }
    } catch (err) {
      console.error('Duplicate form error:', err);
      alert(err.response?.data?.error || 'Failed to duplicate form');
    }
  };

  if (loading) {
    return (
      <AdminLayout activeSection="FORMS">
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="FORMS">
      <style>
        {`
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-fadeInRight {
            animation: fadeInRight 0.3s ease-out;
          }
        `}
      </style>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error notification */}
      {showError && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}

      <div className="w-full">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">FORMS</h1>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 text-white px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-teal-800 text-white">
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                    Form Name
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                    Campaigns
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                    Fields
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                    Entries
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {forms.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                      No forms created yet. Create your first form to start collecting entries.
                    </td>
                  </tr>
                ) : (
                  forms.map((form) => (
                    <tr 
                      key={form.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/forms/${form.id}/edit`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{form.form_name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 line-clamp-2">
                          {form.description || 'No description'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {form.campaign_names ? (
                          <div>
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                              {form.campaign_names.split(', ').length} campaign{form.campaign_names.split(', ').length > 1 ? 's' : ''}
                            </span>
                            <div className="text-xs text-gray-500 mt-1">
                              {form.campaign_names}
                            </div>
                          </div>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Not assigned
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">{form.field_count || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm text-gray-900">{form.total_response_count || form.response_count || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/forms/${form.id}/edit`);
                            }}
                            className="text-teal-600 hover:text-teal-800"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateForm(form.id, form.form_name);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Duplicate"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteForm(form.id, form.name);
                            }}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Footer with Add Button */}
          <div className="px-6 py-4 flex justify-between items-center">
            <button
              onClick={() => setShowCreateModal(true)}
              className="w-12 h-12 bg-teal-800 text-white rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors shadow-lg"
              title="Create New Form"
            >
              <Plus className="w-8 h-8" />
            </button>

            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-gray-700">
                {forms.length} form{forms.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Create Form Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
            <div className="relative bg-white rounded-3xl shadow-xl p-8 max-w-md w-full mx-4">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Create New Form</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Entry Form"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    rows={3}
                    placeholder="Brief description of this form"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '' });
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateForm}
                  className="px-6 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors"
                >
                  Create & Edit Form
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default Forms;